import React from "react";
import { Input as AntInput } from "antd";

interface ImageAltEditorProps {
  images: string[];
  alts?: Record<string, string>;
  onChange: (alts: Record<string, string>) => void;
}

// Keyed by image URL (not array index) so alt text stays attached to the
// right photo through UploadImage's drag-reorder/remove, which only ever
// emits a new `images` array - there's no per-item callback to keep an
// index-based list in sync (Frankie, Oct 2026).
//
// Peter, Oct 2026: entering any alt text failed to save with an error.
// Root cause: imageAlts is a Mongoose Map, and Mongoose rejects any Map
// key containing a "." - every image URL has one (domain, file
// extension), so this failed every time, for every photo. Dots are
// encoded out of the key before it ever reaches the form/API; the web
// site's own imageAlts[src] lookup (ProductImageGallery.tsx) encodes the
// same way to match.
const toAltKey = (url: string) => url.replace(/\./g, "%2E");

const ImageAltEditor: React.FC<ImageAltEditorProps> = ({ images, alts = {}, onChange }) => {
  if (!images || images.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 mt-1">
      <label className="block text-xs font-medium text-slate-600">
        Alt Text (describe what&apos;s in each photo - helps accessibility and image search)
      </label>
      {images.map((url) => (
        <div key={url} className="flex items-center gap-2">
          <img
            src={url}
            alt=""
            className="w-10 h-10 object-cover rounded border border-slate-200 flex-shrink-0"
          />
          <AntInput
            placeholder="e.g. Bronze fox sculpture standing on a stone plinth"
            value={alts[toAltKey(url)] || ""}
            onChange={(e) => onChange({ ...alts, [toAltKey(url)]: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
};

export default ImageAltEditor;
